import Controller from '@ember/controller';
import { dropTask } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import { computed, action } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import RouterService from '@ember/routing/router-service';
import DS from 'ember-data';
import { isEmpty } from '@ember/utils';
import CampaignModel, { TYPE_ICONS, TYPE_NAMES } from 'cio/models/campaign';
import SetupGuide from 'cio/models/setup-guide';
import AnalyticsService from 'cio/services/analytics';
import EnvironmentService from 'cio/services/environment';
import NewsletterModel from 'cio/models/newsletter';
import ArrayProxy from '@ember/array/proxy';
import CampaignTemplatesService, { CAMPAIGN_TEMPLATES } from 'cio/services/campaign-templates';

type MessagingOptionDetail = {
  id: string;
  category?: string;
  image?: string;
  new?: boolean;
  templateId?: string;
  skipPreview?: boolean;
};

type MessagingOptionMeta = {
  name: string;
  description: string;
  type?: string;
};

export type MessagingOption = {
  detail: MessagingOptionDetail;
  campaign?: MessagingOptionMeta;
  newsletter?: MessagingOptionMeta;
};

const NEW_CAMPAIGN_ID = 'new_campaign';
const NEW_NEWSLETTER_ID = 'new_newsletter';

const SAMPLE_CAMPAIGN_OPTIONS_ID: MessagingOptionDetail[] = [
  {
    id: '1',
    image: new URL('cio/assets/images/setup-guide/campaign-thumb--onboarding-campaign.png', import.meta.url).toString(),
  },
  {
    id: '2',
    image: new URL(
      'cio/assets/images/setup-guide/campaign-thumb--reengage-inactive-users.png',
      import.meta.url,
    ).toString(),
  },
  {
    id: '3',
    image: new URL('cio/assets/images/setup-guide/campaign-thumb--convert-trial-users.png', import.meta.url).toString(),
  },
  {
    id: '4',
    image: new URL(
      'cio/assets/images/setup-guide/campaign-thumb--transactional-receipts.png',
      import.meta.url,
    ).toString(),
  },
  {
    id: '5',
    image: new URL(
      'cio/assets/images/setup-guide/campaign-thumb--anniversary-campaign.png',
      import.meta.url,
    ).toString(),
  },
];

const SAMPLE_CAMPAIGN_OPTIONS_EMAIL: MessagingOptionDetail[] = [
  {
    id: '3',
    image: new URL('cio/assets/images/setup-guide/campaign-thumb-re-engage.png', import.meta.url).toString(),
  },
  {
    id: '4',
    image: new URL(
      'cio/assets/images/setup-guide/campaign-thumb-convert-leads-to-free-trialers.png',
      import.meta.url,
    ).toString(),
  },
];

const SAMPLE_NEWSLETTER_OPTIONS_EMAIL: MessagingOptionDetail[] = [
  {
    id: '1',
    image: new URL('cio/assets/images/setup-guide/newsletter-thumb-featured-article.png', import.meta.url).toString(),
  },
  {
    id: '2',
    image: new URL('cio/assets/images/setup-guide/newsletter-thumb-product-update.png', import.meta.url).toString(),
  },
];

const START_FROM_SCRATCH_MESSAGING_OPTIONS: MessagingOption[] = [
  {
    detail: {
      new: true,
      id: NEW_CAMPAIGN_ID,
    },
    campaign: {
      name: 'Start with a campaign',
      description:
        'A great place to start if you want to explore the workflow builder or create a campaign you already have in mind.',
    },
  },
  {
    detail: {
      new: true,
      id: NEW_NEWSLETTER_ID,
    },
    campaign: {
      name: 'Start with a newsletter',
      description:
        'Start with a single email to a group of people at once, like product updates or one-time promotions.',
    },
  },
];

export default class SetupGuideCreateCampaignController extends Controller {
  @service store!: DS.Store;
  @service router!: RouterService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service campaignTemplates!: CampaignTemplatesService;

  model!: SetupGuide;

  selectedMessagingOption!: MessagingOption | null;
  name!: string;

  setDefaults() {
    this.set('name', '');
    this.set('selectedMessagingOption', null);
  }

  fetchCampaignsTask = dropTask(async () => {
    let campaigns: ArrayProxy<CampaignModel> = await this.store.query('campaign', { sample: true, sort: 'name' });
    let campaignOptions = this.environment.current.canIdentifyById
      ? SAMPLE_CAMPAIGN_OPTIONS_ID
      : SAMPLE_CAMPAIGN_OPTIONS_EMAIL;

    let mappedCampaigns = campaigns
      .filter((campaign) => campaign.sample)
      .map((campaign) => {
        let detail = campaignOptions.findBy('id', campaign.id);

        return {
          detail,
          campaign,
        };
      })
      .filter((campaign) => campaign.detail);

    return mappedCampaigns;
  });

  fetchNewslettersTask = dropTask(async () => {
    if (this.environment.current.canIdentifyById) {
      return [];
    }

    let newsletters: ArrayProxy<NewsletterModel> = await this.store.query('newsletter', { tags: ['sample'] });
    let mappedNewsletters = newsletters.map((newsletter) => {
      let detail = SAMPLE_NEWSLETTER_OPTIONS_EMAIL.findBy('id', newsletter.id);

      return {
        detail,
        newsletter,
      };
    });

    return mappedNewsletters;
  });

  @readOnly('fetchCampaignsTask.last.value') campaigns: MessagingOption[] = [];
  @readOnly('fetchNewslettersTask.last.value') newsletters: MessagingOption[] = [];

  readonly normalizedCampaignTemplates = CAMPAIGN_TEMPLATES.filter((template) => template.templateId != null).map(
    (template) => {
      return {
        detail: {
          id: NEW_CAMPAIGN_ID,
          image: template.image,
          templateId: template.templateId as string,
          skipPreview: true,
        },
        campaign: {
          name: template.title,
          description: template.description,
          typeName: template.type ? TYPE_NAMES[template.type] : null,
          icon: template.type ? TYPE_ICONS[template.type] : null,
        },
      };
    },
  );

  @computed('campaigns.[]', 'newsletters.[]')
  get messagingOptions(): MessagingOption[] {
    return [
      ...START_FROM_SCRATCH_MESSAGING_OPTIONS,
      ...(this.newsletters ?? []),
      ...(this.campaigns ?? []),
      ...(this.normalizedCampaignTemplates ?? []),
    ];
  }

  @computed('selectedMessagingOption', 'name')
  get canCreateMessagingOption() {
    if (this.selectedMessagingOption?.detail.new) {
      return !isEmpty(this.name);
    }

    return !isEmpty(this.selectedMessagingOption);
  }

  @computed('selectedMessagingOption')
  get isNewsletterSelected() {
    if (!this.selectedMessagingOption) {
      return false;
    }

    return this.selectedMessagingOption.detail.id === NEW_NEWSLETTER_ID || this.selectedMessagingOption.newsletter;
  }

  @action
  selectMessagingOption(option: MessagingOption) {
    this.setDefaults();
    this.set('selectedMessagingOption', option);
  }

  @action
  selectAndCreateMessagingOption(option: MessagingOption) {
    this.selectMessagingOption(option);

    // @ts-ignore task
    this.createTask.perform(option);
  }

  createTask = dropTask(async (option: MessagingOption) => {
    let { detail, newsletter } = option;
    if (detail.id === NEW_NEWSLETTER_ID || newsletter) {
      await this.findOrCreateAndVisitNewsletter.perform(detail.id);
    } else if (detail.templateId != null) {
      let campaignId = await this.campaignTemplates.createCampaignFromTemplate.perform(detail.templateId);

      let campaign = await this.store.findRecord('campaign', campaignId);
      await this.model.assignFirstMessage(campaign);
      this.router.transitionTo('campaigns.campaign.setup.workflow.actions', campaignId);
    } else {
      await this.findOrCreateAndVisitCampaign.perform(detail.id);
    }
  });

  findOrCreateAndVisitNewsletter = dropTask(async (id: string) => {
    let newsletter: NewsletterModel | null;

    if (id === NEW_NEWSLETTER_ID) {
      newsletter = this.store.createRecord('newsletter', { name: this.name });
    } else {
      newsletter = this.store.peekRecord('newsletter', id);
    }

    if (!newsletter) {
      throw new Error(`Could not find newsletter with an id of ${id}`);
    }

    await newsletter.save();
    await this.model.assignFirstMessage(newsletter);

    this.analytics.track('broadcast', 'create_started', { source: 'Onboarding' });
    this.router.transitionTo('broadcasts.newsletter', newsletter.id);
  });

  findOrCreateAndVisitCampaign = dropTask(async (id?: string) => {
    let campaign: CampaignModel | null;

    if (id === NEW_CAMPAIGN_ID) {
      campaign = this.store.createRecord('campaign', { name: this.name, type: 'none' });
    } else if (id) {
      campaign = this.store.peekRecord('campaign', id);
    } else {
      throw new Error('Cannot create a campaign without an id');
    }

    if (!campaign) {
      throw new Error(`Could not find campaign with an id of ${id}`);
    }

    await campaign.save();
    await this.model.assignFirstMessage(campaign);

    this.analytics.track('campaign', 'create_started', { source: 'Onboarding' });
    this.router.transitionTo('campaigns.campaign', campaign.id);
  });
}
